import { FilterType, IHeadCell, OperatorFilterEnum, SelectFilterModelSimple } from '../../../common/types';
import { formatDateTimeToShowUserFromSaleOrderApiValue, formatDateToShowUser } from '../../../common/helpers';
import { IFlightAndFlightAncillaryIssueSummary } from '../../types';
import { FlightAncillaryTypeEnum } from '../../../salesOrder/types';

export enum FlightTicketTypeEnum {
	Emision = 'emision',
	Reemision = 're_emision',
}

export enum TicketingStatusEnum {
	Pendiente = 'pending',
	Emitido = 'issued',
}

export interface ITicketingStatusOption {
	id: string;
	name: string;
}

export const ticketingStatusOptions: ITicketingStatusOption[] = [
	{
		id: TicketingStatusEnum.Pendiente,
		name: 'Pendiente',
	},
	{
		id: TicketingStatusEnum.Emitido,
		name: 'Emitido',
	},
];

export interface IETicketTypeOption {
	id: string;
	name: string;
}

export const eticketTypeOptions: IETicketTypeOption[] = [
	// Ticket types
	{
		id: FlightTicketTypeEnum.Emision,
		name: 'Emisión',
	},
	{
		id: FlightTicketTypeEnum.Reemision,
		name: 'Reemisión',
	},

	// Ancillary types
	{
		id: FlightAncillaryTypeEnum.Seat,
		name: 'Asientos',
	},
	{
		id: FlightAncillaryTypeEnum.CabinBaggage,
		name: 'Equipaje en cabina',
	},
	{
		id: FlightAncillaryTypeEnum.CheckedBaggage,
		name: 'Equipaje en bodega',
	},
	{
		id: FlightAncillaryTypeEnum.Pet,
		name: 'Mascota',
	},
	{
		id: FlightAncillaryTypeEnum.InFlightEntertainment,
		name: 'Entretenimiento a bordo',
	},
	{
		id: FlightAncillaryTypeEnum.Meal,
		name: 'Comidas',
	},
	{
		id: FlightAncillaryTypeEnum.SpecialLuggage,
		name: 'Equipajes especiales',
	},
	{
		id: FlightAncillaryTypeEnum.AccompaniedMinor,
		name: 'Menor acompañado',
	},
];

export function getETicketTypeName(id: string): string {
	const option = eticketTypeOptions.find((option) => option.id === id);
	return option ? option.name : id;
}

export function getTicketingStatusName(id: string): string {
	const option = ticketingStatusOptions.find((option) => option.id === id);
	return option ? option.name : id;
}

export const flightAndAncillaryIssueSummaryListHeaderCells: IHeadCell[] = [
	{
		field: 'serviceId',
		sortable: true,
		headerName: 'Service ID',
	},
	{
		field: 'ticketingStatus',
		sortable: true,
		headerName: 'Estado de Emisión',
	},
	{
		field: 'ticketingRequested',
		sortable: true,
		headerName: 'Emisión Solicitada',
	},
	{
		field: 'gds',
		sortable: true,
		headerName: 'GDS',
	},
	{
		field: 'vcCode',
		sortable: true,
		headerName: 'Validating Carrier',
	},
	{
		field: 'ticketingLimitDatetime',
		sortable: true,
		headerName: 'Límite de Emisión',
	},
	{
		field: 'ticketType',
		sortable: true,
		headerName: 'Tipo de Ticket',
	},
	{
		field: 'serviceCost',
		sortable: true,
		headerName: 'Costo del Servicio',
	},
	{
		field: 'serviceReference',
		sortable: true,
		headerName: 'PNR',
	},
	{
		field: 'serviceCreatedAt',
		sortable: true,
		headerName: 'Fecha de Creación',
	},
	{
		field: 'saleOrderId',
		sortable: true,
		headerName: 'Sale Order ID',
	},
	{
		field: 'saleOrderChargedAmount',
		sortable: true,
		headerName: 'Monto Cobrado',
	},
	{
		field: 'saleOrderDealId',
		sortable: true,
		headerName: 'Deal ID',
	},
	{
		field: 'mainPassengerCompleteName',
		sortable: true,
		headerName: 'Pasajero Principal',
	},
	{
		field: 'providerFantasyName',
		sortable: true,
		headerName: 'Proveedor',
	},
	{
		field: 'crmAgencyName',
		sortable: true,
		headerName: 'Agencia CRM',
	},
	{
		field: 'crmAgentCompleteName',
		sortable: true,
		headerName: 'Agente CRM',
	},
];

export const flightAndAncillaryIssueSummaryListFilters = [
	{ label: 'Fecha de creación', type: FilterType.DATE, key: 'serviceCreatedAt' },
	{ label: 'Fecha límite emisión', type: FilterType.DATE, key: 'ticketingLimitDatetime' },
	{ label: 'Service ID', type: FilterType.STRING, key: 'serviceId', operator: OperatorFilterEnum.CONTAINS },
	{ label: 'PNR', type: FilterType.STRING, key: 'serviceReference', operator: OperatorFilterEnum.CONTAINS },
	{ label: 'Sale Order ID', type: FilterType.STRING, key: 'saleOrderId', operator: OperatorFilterEnum.CONTAINS },
	{ label: 'Deal ID', type: FilterType.NUMERIC, key: 'saleOrderDealId', operator: OperatorFilterEnum.EQUALS },
	{
		label: 'Pasajero Principal',
		type: FilterType.STRING,
		key: 'mainPassengerCompleteName',
		operator: OperatorFilterEnum.CONTAINS,
	},
	{ label: 'Proveedor', type: FilterType.STRING, key: 'providerFantasyName', operator: OperatorFilterEnum.CONTAINS },
	{ label: 'Validating carrier', type: FilterType.STRING, key: 'vcCode', operator: OperatorFilterEnum.EQUALS },
	{ label: 'Agencia CRM', type: FilterType.STRING, key: 'crmAgencyName', operator: OperatorFilterEnum.CONTAINS },
	{ label: 'Agente CRM', type: FilterType.STRING, key: 'crmAgentCompleteName', operator: OperatorFilterEnum.CONTAINS },
	new SelectFilterModelSimple('Estado de Emisión', 'ticketingStatus', ticketingStatusOptions),
	new SelectFilterModelSimple('Tipo de Ticket', 'ticketType', eticketTypeOptions),
];

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const transformValue = (key: string, value: any, row: IFlightAndFlightAncillaryIssueSummary): any => {
	switch (key) {
		case 'serviceCreatedAt':
			return formatDateTimeToShowUserFromSaleOrderApiValue(row.serviceCreatedAt);
		case 'ticketingLimitDatetime':
			return formatDateTimeToShowUserFromSaleOrderApiValue(row.ticketingLimitDatetime);
		case 'saleOrderTravelDepartureDate':
			return row.saleOrderTravelDepartureDate ? formatDateToShowUser(row.saleOrderTravelDepartureDate) : '-';
		case 'ticketingStatus':
			return getTicketingStatusName(row.ticketingStatus);
		case 'ticketType':
			return getETicketTypeName(row.ticketType);
		case 'ticketingRequested':
			return row.ticketingRequested ? 'Sí' : 'No';
		case 'serviceCost':
			return row.serviceCost != null ? `$${row.serviceCost.toLocaleString()}` : '-';
		case 'saleOrderChargedAmount':
			return row.saleOrderChargedAmount != null ? `$${row.saleOrderChargedAmount.toLocaleString()}` : '-';
		case 'providerFantasyName':
			return row.providerFantasyName && row.providerBusinessName
				? row.providerFantasyName + ` (${row.providerBusinessName})`
				: row.providerFantasyName || '-';
		case 'gds':
			return row.gds || '-';
		case 'vcCode':
			return row.vcCode || '-';
		case 'crmAgencyName':
			return row.crmAgencyName || '-';
		case 'crmAgentCompleteName':
			return row.crmAgentCompleteName || '-';
		default:
			return value;
	}
};
